/*
* Association view for authenticated users
*/

import { compose } from 'redux';
import { connect } from 'react-redux';
import { connectRequest } from 'redux-query';
import { Link } from 'react-router-dom';
import { push } from 'react-router-redux';
import { Row, Button, ButtonToolbar, Well, FormControl, FormGroup, Modal, ControlLabel, Form as RBForm, Label as RBLabel } from 'react-bootstrap';
import { withRouter } from 'react-router-dom';
import * as _ from 'lodash';
import * as Form from 'react-formal';
import moment from 'moment';
import * as React from 'react';
import * as yup from 'yup';

import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import { NavigationLinks, EmailLinkComponent } from '../common/navigation';
import { PortalRoles } from '../invite/create-invite';
import { SectionField, EntityDetailSection, ValColDisplayModes } from '../common/entitydetail/entitydetailsection';
import { SelectOptions } from '../common/selectOptions';
import { statesList, countriesList } from '../config/geolocations';
import { UserUtilities } from '../user/user-utilities';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import { WhiteBoxSection } from '../common/whiteboxsection';
import * as AppComp from '../common/app-component-base';
import * as AppUtilities from '../common/apputilities';
import * as LK from '../config/apilookupkeys';
import * as MutateActions from '../actions/mutate';
import * as ReduxQueries from '../queries';
import ApiEndpoints from '../config/apiendpoints';
import Auth from '../auth/auth';
import CopyAddressToClipboard from '../common/copyaddresstoclipboard';
import EntityNotes from '../common/entitydetail/entitynotes';
import GoogleMapEmbed from '../common/googlemapembed';
import * as KendoWrappers from '../common/kendoforms/componentwrappers';

import './associationstyles.css';

export const Div = ({ ...rest }) => (<div {...rest} />);

export const ContactControl = ({ contact, appText, defaultPhoneFormatCountry = Formatters.DefaultPhoneFormatCountry, ...rest }) => {
    if (!appText || !contact) { return (<div />); }

    let contactDetails: Array<any> = [];

    contactDetails.push(Formatters.composeClientContactFormal(contact));

    const email = _.get(contact, 'email', '');
    if (email) {
        const link = (<EmailLinkComponent email={email} caption={`${appText('app.common.contacts.send-email-to', false)} ${email}`} />);
        contactDetails.push(link);
    }

    const phone = _.get(contact, 'phone', '');
    if (phone) {
        const country = _.get(contact, 'phone', defaultPhoneFormatCountry);
        contactDetails.push(Formatters.formatPhoneNumber(phone, country));
    }

    return (
        <div>{contactDetails.map((c, i) => (<div key={i}>{c}</div>))}</div>
    );
};

export const ClientControl = ({ client, appText, ...rest }) => {
    if (!client) { return (<div>{appText('client-info.placeholder')}</div>); }

    let clientDetails: Array<string> = [];

    const clientName = _.get(client, 'name', '');
    if (clientName) {
        clientDetails.push(clientName);
    }

    const location = _.get(client, 'location', undefined);
    if (location) {
        const address1 = _.get(location, 'address1', '');
        if (address1) {
            clientDetails.push(address1);
        }

        const address2 = _.get(location, 'address2', '');
        if (address2) {
            clientDetails.push(address2);
        }

        const address = Formatters.composeLocationCityStateZipCountry(location);
        if (address) { clientDetails.push(address); }
    }

    return (
        <div>{clientDetails.map((c, i) => (<div key={i}>{c}</div>))}</div>
    );
};

const FormSectionInputTemplate = (label, fieldName, required, formInputControl, labelColXs = 4, valColXs = 5) => (
    <Form.Field name={fieldName} required={required} type={FormGroup}>
        <Col componentClass={ControlLabel} className={required ? 'required' : ''} xs={labelColXs}>{label}</Col>
        <Col xs={valColXs}>{formInputControl}</Col>
    </Form.Field>
);

const FormSectionField = (label, fieldName, displayValue, defaultValue, editMode, required, placeholder, refRegisterFunc, labelColXs = 4, labelColValXs = 5) => {
    const formInputControl = (
        <FormControl
            type="text"
            required={required}
            defaultValue={defaultValue}
            placeholder={placeholder}
            ref={refRegisterFunc}
        />
    );

    const renderTemplate = FormSectionInputTemplate(label, fieldName, required, formInputControl);

    return SectionField.CreateSingle(label, displayValue, editMode, (props) => renderTemplate);
};

const FormSectionFieldSelect = (label, fieldName, displayValue, defaultValue, options, editMode, required, placeholder, refRegisterFunc, labelColXs = 4, labelColValXs = 5) => {
    const formInputControl = (
        <FormControl
            componentClass="select"
            required={required}
            defaultValue={defaultValue}
            placeholder={placeholder}
            ref={refRegisterFunc}
        >
            {options}
        </FormControl>
    );

    const renderTemplate = FormSectionInputTemplate(label, fieldName, required, formInputControl);

    return SectionField.CreateSingle(label, displayValue, editMode, (props) => renderTemplate);
};

export interface IAssociationDetailProps extends AppComp.IAppComponentProps {
    associationId?: any;
    association: any;
    currentOrderId: any;
    portalInvites?: any;
    navigateTo?: any;
    newOrder?: any;
    deleteAssociation?: any;
}

class Association extends AppComp.AppComponent<IAssociationDetailProps, any> {
    public readonly placeholderText = this.appTextSelectOptionPlaceholder();
    public readonly placeholderDateInput = this.appTextDateInputPlaceholder();
    public readonly placeholderNumberInput = this.appTextNumberInputPlaceholder();
    public originalFormDataCache = {};

    public controlRefs: any = {};

    public constructor(props: any) {
        super(props, 'app.association-detail.');

        this.state = { editing: false, showModalDeleteAssociation: false, verifyDelete: '', formChanges: {}, };
    }

    public componentDidMount() {
        const enforce = this.enforceAdminOrEmployeeOnly();

        if (enforce) { this.props.navigateTo(NavigationLinks.userDashboard()); }
    }

    public isEditing = (): boolean => this.state.editing;

    // Handlers
    public handleEditButton(e: any) {
        const { associationId } = this.props;
        this.props.navigateTo(NavigationLinks.associationEdit(associationId));
    }

    public handleInventoryButton(e: any) {
        const { associationId } = this.props;
        this.props.navigateTo(NavigationLinks.associationInventory(associationId));
    }

    public handlePropertyDescriptionButton(e: any) {
        const { associationId } = this.props;
        this.props.navigateTo(NavigationLinks.associationPropertyReportPdf(associationId));
    }

    public handleViewDisclosureButton(e: any) {
        const { associationId } = this.props;
        this.props.navigateTo(NavigationLinks.associationCaliforniaDisclosureReportPdf(associationId));
    }

    public handleViewOrderButton(e: any, orderId: any) {
        this.props.navigateTo(NavigationLinks.orderDetail(orderId));
    }

    public handleAddOrderButton(e: any, assocId: any, clientId: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        // Create order for new assoc
        const createOrderData = _.merge(
            {},
            { association: { associationId: assocId } },
            { client: { clientId: clientId } },
        );

        this.props.newOrder(createOrderData, token, false).then(orderResponse => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (orderResponse.status >= 200 && orderResponse.status < 300) {
                // this.originalFormDataCache = {};

                // FORCE RELOAD
                const newOrderId = _.get(orderResponse, 'body.orderId');

                this.props.navigateTo(NavigationLinks.orderEdit(newOrderId));
            } else {
                // Error during creation...
            }
        });
    }

    public handleShowDeleteAssociationModalButton() {
        this.setState({ showModalDeleteAssociation: true });
    }

    public handleCancelAndCloseModalDeleteAssociation() {
        this.setState({ showModalDeleteAssociation: false });
    }

    public handleDeleteAssociationButton(e: any, assocId: any, userObjId: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        // Create order for new assoc
        const deleteAssociationData = _.merge(
            {},
            { association: { associationId: assocId } },
            { userObjId: { userObjId: userObjId } },
        );

        this.props.deleteAssociation(deleteAssociationData, token, false).then(orderResponse => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (orderResponse.status >= 200 && orderResponse.status < 300) {

                this.props.navigateTo(NavigationLinks.associationSearch());
            } else {
                // Error during creation...
            }
        });
    }

    public handleViewPortalButton(e: any) {
        const { associationId } = this.props;
        this.props.navigateTo(NavigationLinks.portalAssociationReport(associationId));
    }

    public handleReserveStudyButton(e: any) {
        const { associationId } = this.props;
        this.props.navigateTo(NavigationLinks.reserveStudy(associationId));
    }

    public handleReserveStudyPdfButton(e: any) {
        const { associationId } = this.props;
        this.props.navigateTo(NavigationLinks.associationReportPdf(associationId));
    }

    public handleFormInputChange(field: string, value: any) {
        // console.log('handleForminputchange', { field: field, value: value });

        const getChangesResult = this.getFormChanges(field, value);
        // console.log('handleForminputchange getChangesResult', {getChangesResult: getChangesResult, e: e, x: x });
        if (getChangesResult.applyChanges) {
            this.setState({
                formChanges: getChangesResult.formChanges
            });
        }
    }

    // Helpers
    public getFormChanges(e: any, x: any) {
        let result = {
            applyChanges: false,
            formChanges: {},
            formChangesResult: {},
        };

        let fieldName = '';
        let newValue;

        let formChangesCopy = _.cloneDeep(this.state.formChanges);

        if (_.get(e, 'target.props.name')) {
            // Kendo form element
            if (_.has(e, 'target.combobox')) {
                // combobox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'target.value');
            } else {
                // assume textbox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'value');
            }
        } else {
            const eventTargetType = _.get(e, 'target.type', '');

            switch (eventTargetType) {
                case 'textarea':
                case 'text':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                case 'kendo-dropdownlist':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value', '');
                    break;

                case 'virtualized-select':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(x, 'value');
                    break;

                case 'delete-button':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                default:
                    // console.log(`*** handleFormInputChange switch hit default for eventTargetType ${eventTargetType}`);
                    break;
            }
        }

        // console.log(`handleFormInputChange continuing 1 fieldName ${fieldName} newValue ${newValue}`, { e: e, x: x });

        if (!fieldName) { return result; }

        let applyChange = false;
        let origVal = _.get(this.originalFormDataCache, fieldName);
        let formChangesResult = {};
        if (_.isArray(origVal)) {
            if (_.isArray(newValue)) {
                if (!_.isEqual(origVal, newValue)) {
                    _.set(formChangesCopy, fieldName, _.cloneDeep(newValue));
                    _.set(formChangesResult, fieldName, _.cloneDeep(newValue));
                    applyChange = true;
                } else {
                    applyChange = false;
                }
            } else {
                if (_.includes(origVal, newValue)) {
                    // console.log(`original array already contained val ${newValue} origVal`, origVal);
                    applyChange = false;
                } else {
                    const formChangesArr = _.get(formChangesCopy, fieldName, []);
                    // console.log(`${fieldName} formChangesArr`, formChangesArr);
                    if (_.includes(formChangesArr, newValue)) {
                        // console.log(`formChangesArr field ${fieldName} array already contains val ${newValue} formChangesArr`, formChangesArr);
                        applyChange = false;
                    } else {
                        formChangesArr.push(newValue);
                        _.set(formChangesCopy, fieldName, _.cloneDeep(formChangesArr));
                        _.set(formChangesResult, fieldName, _.cloneDeep(formChangesArr));
                        applyChange = true;
                        // console.log(`formChangesArr field ${fieldName} array DID NOT have val ${newValue}, applying change`, formChangesArr);
                    }
                }
            }
        } else {
            if (String(newValue) !== String(origVal)) {
                // New value
                _.set(formChangesCopy, fieldName, newValue);
                _.set(formChangesResult, fieldName, newValue);
                applyChange = true;
            } else {
                // Same as orig val
                if (_.has(formChangesCopy, fieldName)) {
                    _.unset(formChangesCopy, fieldName);
                    _.set(formChangesResult, fieldName, newValue);
                    applyChange = true;
                }
            }
        }

        if (applyChange) {
            result.applyChanges = true;
            result.formChanges = _.cloneDeep(formChangesCopy);
            result.formChangesResult = _.cloneDeep(formChangesResult);
        }

        // console.log(`getFormChanges returns result`, result);

        return result;
    }
    public registerInputRef(e: any) {
        // console.log('registerInputRef', e);
    }

    public getClient(clientId: number) {
        const { lookups } = this.props;

        return _.find(_.get(lookups, 'clients', []), ['clientId', clientId]);
    }

    public getClientContacts(client: any) {
        let result = [];

        if (client && AppUtilities.isArrayWithLength(_.get(client, 'contacts'))) {
            result = _.get(client, 'contacts', []).map(c => {
                return {
                    contactId: _.get(c, 'contactId'),
                    name: Formatters.composeClientContactFormal(c),
                    sort: _.get(c, 'lastName')
                };
            });
        }

        return result;
    }

    public getSelectOptions = (lookups, lookupName, valueFieldName, typeIdFieldName) => {
        return SelectOptions.prepareSelectOptionsNew(
            _.get(lookups, lookupName, []),
            valueFieldName,
            valueFieldName,
            typeIdFieldName,
            'disabled',
            typeIdFieldName,
            this.placeholderText,
            '',
            -99999,
            false);
    }

    public buildAssociationSection(sections: any, assoc: any) {
        let result: Array<any> = [];

        if (!sections || !assoc) { return result; }

        const associationNameLink = (
            <div>
                <div>
                    <span>{Formatters.getStr(assoc, 'name')}</span> <span className="association-details-association-id">({Formatters.getStr(assoc, 'associationId')})</span>
                </div>

                <div>
                    <CopyAddressToClipboard locationObj={sections.location} />
                </div>
            </div>
        );

        // Association name
        result.push(FormSectionField(
            this.appText('association-name'),
            'name',
            associationNameLink,
            '', // assocName,
            this.isEditing(),
            true,
            this.appText('association-name.placeholder'),
            (e) => { this.registerInputRef(e); })
        );

        result.push((
            <Form.Field name="name" type="text" />
        ));

        return result;
    }

    // Build association detail/form sections
    public buildAddressSection(sections: any, assoc: any, lookups: any) {
        let result: Array<any> = [];

        if (!sections || !assoc || !lookups) { return result; }
        // 20180712 changing sortbyfield from sort to name
        const stateListOptions = SelectOptions.prepareSelectOptions(statesList, 'name', 'stateCode', 'sort', 'disabled', 'name', this.placeholderText, '', '', true);
        const countryListOptions = SelectOptions.prepareSelectOptions(countriesList, 'name', 'countryCode', 'sort', 'disabled', 'name', this.placeholderText, '', '', true);

        // Address block
        result.push(FormSectionField(
            this.appTextCommon('location.address'),
            'location.address1',
            _.get(assoc, 'location.address1'),
            _.get(assoc, 'location.address1'),
            this.isEditing(),
            true,
            this.appTextCommon('location.address.placeholder'),
            (e) => { this.registerInputRef(e); })
        );

        result.push(FormSectionField(
            this.appTextCommon('location.address2'),
            'location.address2',
            _.get(assoc, 'location.address2'),
            _.get(assoc, 'location.address2'),
            this.isEditing(),
            false,
            this.appTextCommon('location.address2.placeholder'),
            (e) => { this.registerInputRef(e); })
        );

        result.push(FormSectionField(
            this.appTextCommon('location.city'),
            'location.city',
            _.get(assoc, 'location.city'),
            _.get(assoc, 'location.city'),
            this.isEditing(),
            true,
            this.appTextCommon('location.city.placeholder'),
            (e) => { this.registerInputRef(e); })
        );

        let states = stateListOptions.map((c, i) => (<option key={i} value={c.value} disabled={c.disabled}>{c.label}</option>));
        let countries = countryListOptions.map((c, i) => (<option key={i} value={c.value} disabled={c.disabled}>{c.label}</option>));

        result.push(FormSectionFieldSelect(
            this.appTextCommon('location.state'),
            'location.stateCode',
            _.get(assoc, 'location.stateCode'),
            _.get(assoc, 'location.stateCode'),
            states,
            this.isEditing(),
            true,
            this.appTextCommon('location.state.placeholder'),
            (e) => { this.registerInputRef(e); })
        );

        result.push(FormSectionField(
            this.appTextCommon('location.zip'),
            'location.postalCode',
            _.get(assoc, 'location.postalCode'),
            _.get(assoc, 'location.postalCode'),
            this.isEditing(),
            true,
            this.appTextCommon('location.zip.placeholder'),
            (e) => { this.registerInputRef(e); })
        );

        result.push(FormSectionFieldSelect(
            this.appTextCommon('location.country'),
            'location.countryCode',
            _.get(assoc, 'location.countryCode', ''),
            _.get(assoc, 'location.countryCode', ''),
            countries,
            this.isEditing(),
            false,
            this.appTextCommon('location.country.placeholder'),
            (e) => { this.registerInputRef(e); })
        );

        return result;
    }

    public buildDetailsSection(sections: any, assoc: any, lookups: any) {
        let result: Array<any> = [];

        if (!sections || !assoc || !lookups) { return result; }

        const associationTypeOptions = this.renderSelectOptions(SelectOptions.prepareSelectOptionsNew(
            _.get(lookups, 'associationTypes', []),
            'name',
            'associationTypeId',
            'associationTypeId',
            'disabled',
            'name',
            this.placeholderText,
            '',
            '',
            true,
            true,
            true
        ));

        result.push(FormSectionFieldSelect(
            this.appText('association-type'),
            'associationType.associationTypeId',
            _.get(assoc, 'associationType.name', ''),
            _.get(assoc, 'associationType.associationTypeId', ''),
            associationTypeOptions,
            this.isEditing(),
            true,
            '',
            (e) => { this.registerInputRef(e); })
        );

        const companyOptions = this.renderSelectOptions(SelectOptions.prepareSelectOptionsNew(
            _.get(lookups, 'companies', []),
            'name',
            'companyId',
            'sort',
            'disabled',
            'companyId',
            this.placeholderText,
            '',
            '',
            true,
            true,
            true
        ));

        result.push(FormSectionFieldSelect(
            this.appText('company'),
            'company.companyId',
            _.get(assoc, 'company.name', ''),
            _.get(assoc, 'company.companyId', ''),
            companyOptions,
            this.isEditing(),
            true,
            '',
            (e) => { this.registerInputRef(e); })
        );

        result.push(FormSectionField(
            this.appText('fiscal-year-end'),
            'details.fiscalYearEnd',
            _.get(assoc, 'details.fiscalYearEnd', ''),
            _.get(assoc, 'details.fiscalYearEnd', ''),
            this.isEditing(),
            false,
            this.appTextDateInputPlaceholder(),
            (e) => { this.registerInputRef(e); })
        );

        // result.push(FormSectionFieldCheckbox(
        //     this.appText('inactive'),
        //     'details.inactive',
        //     _.get(assoc, 'details.inactive', false),
        //     _.get(assoc, 'details.inactive', false),
        //     this.isEditing(),
        //     false,
        //     '',
        //     (e) => { this.registerInputRef(e); })
        // );

        return result;
    }

    public buildClientContactSection(sections: any, assoc: any, lookups: any) {
        let result: Array<any> = [];

        if (!sections || !assoc || !lookups) { return result; }

        // const clientLink = (<NavLinkComponent caption={_.get(assoc, 'client.name')} linkDetails={{ to: NavigationLinks.clientDetail(_.get(assoc, 'client.clientId')), title: this.appText('view-client') }} />);
        const contactInfo = (<ContactControl contact={_.get(assoc, 'contact')} appText={(a, b) => this.appText(a, b)} />);

        const clientOptions = this.renderSelectOptions(SelectOptions.prepareSelectOptionsNew(
            _.get(lookups, 'clients', []),
            'name',
            'clientId',
            'sort',
            'disabled',
            'name',
            this.placeholderText,
            '',
            '',
            true,
            true,
            true
        ));

        result.push(FormSectionFieldSelect(
            this.appText('client'),
            'client.clientId',
            _.get(assoc, 'client.name', ''),
            _.get(assoc, 'client.clientId', ''),
            clientOptions,
            this.isEditing(),
            true,
            '',
            (e) => { this.registerInputRef(e); })
        );

        let clientId = _.get(assoc, 'client.clientId', -1);
        const client = this.getClient(clientId);
        const contactOptions = this.renderSelectOptions(SelectOptions.prepareSelectOptionsNew(
            this.getClientContacts(client),
            'name',
            'contactId',
            'sort',
            'disabled',
            'name',
            this.placeholderText,
            '',
            -1,
            true,
            true,
            true
        ));

        result.push(FormSectionFieldSelect(
            this.appText('manager-contact'),
            'contact.contactId',
            contactInfo,
            _.get(assoc, 'contact.contactId', 0),
            contactOptions,
            this.isEditing(),
            true,
            '',
            (e) => { this.registerInputRef(e); })
        );

        return result;
    }

    public buildPropertyDetailsConstructionInfoSection(sections: any, assoc: any) {
        let result: Array<any> = [];

        // console.log('assoc', assoc);

        if (!sections || !assoc) { return result; }

        result.push(SectionField.CreateSingle(
            this.appText('num-buildings'),
            _.get(assoc, 'details.numberBuildings', ''),
            this.isEditing(),
            (props) => (<div />)
        ));

        result.push(SectionField.CreateSingle(
            this.appText('num-units'),
            _.get(assoc, 'details.numberUnits', ''),
            this.isEditing(),
            (props) => (<div />)
        ));

        result.push(SectionField.CreateSingle(
            this.appText('num-stories'),
            _.get(assoc, 'details.numberStories', ''),
            this.isEditing(),
            (props) => (<div />)
        ));

        result.push(SectionField.CreateSingle(
            this.appText('phases'),
            _.get(assoc, 'details.phases', ''),
            this.isEditing(),
            (props) => (<div />)
        ));

        result.push(SectionField.CreateSingle(
            this.appText('construction-date'),
            _.get(assoc, 'details.constructDate', ''),
            this.isEditing(),
            (props) => (<div />)
        ));

        result.push(SectionField.CreateSingle(
            this.appText('conversion-date'),
            _.get(assoc, 'details.converDate', ''),
            this.isEditing(),
            (props) => (<div />)
        ));

        // console.log(result);

        return result;
    }

    public buildPropertyDetailsAmenitiesCoveredLeftSection(sections: any, assoc: any, lookups: any) {
        let result: Array<any> = [];

        if (!sections || !assoc || !lookups) { return result; }

        result.push(SectionField.CreateSingle(
            this.appText('streets'),
            _.get(assoc, 'details.streets', ''),
            this.isEditing(),
            (props) => (<div />)
        ));

        result.push(SectionField.CreateSingle(
            this.appText('roofs'),
            _.get(assoc, 'details.roofs', ''),
            this.isEditing(),
            (props) => (<div />)
        ));

        result.push(SectionField.CreateSingle(
            this.appText('painted-exteriors'),
            _.get(assoc, 'details.siding', ''),
            this.isEditing(),
            (props) => (<div />)
        ));

        result.push(SectionField.CreateSingle(
            this.appText('fencing'),
            _.get(assoc, 'details.fencing', ''),
            this.isEditing(),
            (props) => (<div />)
        ));

        return result;
    }

    public buildPropertyDetailsAmenitiesCoveredRightSection(sections: any, assoc: any, lookups: any) {
        let result: Array<any> = [];

        if (!sections || !assoc || !lookups) { return result; }

        result.push(SectionField.CreateSingle(
            this.appText('interior-hallways'),
            _.get(assoc, 'details.interiorHalls', ''),
            this.isEditing(),
            (props) => (<div />)
        ));

        result.push(SectionField.CreateSingle(
            this.appText('irrigation'),
            _.get(assoc, 'details.irrigation', ''),
            this.isEditing(),
            (props) => (<div />)
        ));

        result.push(SectionField.CreateSingle(
            this.appText('secured-property'),
            _.get(assoc, 'details.secured', ''),
            this.isEditing(),
            (props) => (<div />)
        ));

        result.push(SectionField.CreateSingle(
            this.appText('clubhouse-rec-room'),
            _.get(assoc, 'details.recRooms'),
            this.isEditing(),
            (props) => (<div />)
        ));

        return result;
    }

    // HOA responsible exterior/grounds checkboxes
    public buildPropertyDetailsAmenitiesResponsibleLeftSection(sections: any, assoc: any, lookups: any) {
        let result: Array<any> = [];

        if (!sections || !assoc || !lookups) { return result; }

        result.push(SectionField.CreateSingle(
            this.appText('trash-enclosures'),
            Formatters.getBool(assoc, 'details.trashEnclosures'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('monument-signs'),
            Formatters.getBool(assoc, 'details.monumentSigns'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('bike-walking-path'),
            Formatters.getBool(assoc, 'details.bikeWalkingPaths'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('street-lighting'),
            Formatters.getBool(assoc, 'details.streetLighting'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('street-signs'),
            Formatters.getBool(assoc, 'details.streetSigns'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('outdoor-furniture'),
            Formatters.getBool(assoc, 'details.outdoorFurniture'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        return result;
    }

    public buildPropertyDetailsAmenitiesResponsibleMiddleSection(sections: any, assoc: any, lookups: any) {
        let result: Array<any> = [];

        if (!sections || !assoc || !lookups) { return result; }

        result.push(SectionField.CreateSingle(
            this.appText('awnings'),
            Formatters.getBool(assoc, 'details.awnings'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('mailboxes'),
            Formatters.getBool(assoc, 'details.mailboxes'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('walkways'),
            Formatters.getBool(assoc, 'details.walkways'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('fire-escapes'),
            Formatters.getBool(assoc, 'details.fireEscapes'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('building-lighting'),
            Formatters.getBool(assoc, 'details.buildingLighting'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('decks-balconies'),
            Formatters.getBool(assoc, 'details.decksBalconies'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        return result;
    }

    public buildPropertyDetailsAmenitiesResponsibleRightSection(sections: any, assoc: any, lookups: any) {
        let result: Array<any> = [];

        if (!sections || !assoc || !lookups) { return result; }

        result.push(SectionField.CreateSingle(
            this.appText('carports'),
            Formatters.getBool(assoc, 'details.carports'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('garages'),
            Formatters.getBool(assoc, 'details.garages'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('exterior-stairways'),
            Formatters.getBool(assoc, 'details.exteriorStairways'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('waterscapes-fountains'),
            Formatters.getBool(assoc, 'details.waterscapesFountains'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('playground-equipment'),
            Formatters.getBool(assoc, 'details.playgroundEquipment'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('gazebos'),
            Formatters.getBool(assoc, 'details.gazebos'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        return result;
    }

    // HOA responsible quantity fields
    public buildPropertyDetailsAmenitiesResponsibleQtyLeftSection(sections: any, assoc: any, lookups: any) {
        let result: Array<any> = [];

        if (!sections || !assoc || !lookups) { return result; }

        result.push(SectionField.CreateSingle(
            this.appText('pools'),
            Formatters.getInt(assoc, 'details.pools'),
            this.isEditing(),
            (props) => (<div />),
        ));

        result.push(SectionField.CreateSingle(
            this.appText('spas'),
            Formatters.getInt(assoc, 'details.spas'),
            this.isEditing(),
            (props) => (<div />),
        ));

        result.push(SectionField.CreateSingle(
            this.appText('wading-pools'),
            Formatters.getInt(assoc, 'details.wadingPools'),
            this.isEditing(),
            (props) => (<div />),
        ));

        result.push(SectionField.CreateSingle(
            this.appText('saunas'),
            Formatters.getInt(assoc, 'details.saunas'),
            this.isEditing(),
            (props) => (<div />),
        ));

        result.push(SectionField.CreateSingle(
            this.appText('marinas-docks'),
            Formatters.getInt(assoc, 'details.marinasDocks'),
            this.isEditing(),
            (props) => (<div />),
        ));

        return result;
    }

    public buildPropertyDetailsAmenitiesResponsibleQtyMiddleSection(sections: any, assoc: any, lookups: any) {
        let result: Array<any> = [];

        if (!sections || !assoc || !lookups) { return result; }

        result.push(SectionField.CreateSingle(
            this.appText('generators'),
            Formatters.getInt(assoc, 'details.generators'),
            this.isEditing(),
            (props) => (<div />),
        ));

        result.push(SectionField.CreateSingle(
            this.appText('water-heaters'),
            Formatters.getInt(assoc, 'details.waterHeaters'),
            this.isEditing(),
            (props) => (<div />),
        ));

        result.push(SectionField.CreateSingle(
            this.appText('boilers'),
            Formatters.getInt(assoc, 'details.boilers'),
            this.isEditing(),
            (props) => (<div />),
        ));

        result.push(SectionField.CreateSingle(
            this.appText('hvac-units'),
            Formatters.getInt(assoc, 'details.hvacUnits'),
            this.isEditing(),
            (props) => (<div />),
        ));

        result.push(SectionField.CreateSingle(
            this.appText('chillers'),
            Formatters.getInt(assoc, 'details.chillers'),
            this.isEditing(),
            (props) => (<div />),
        ));

        return result;
    }

    public buildPropertyDetailsAmenitiesResponsibleQtyRightSection(sections: any, assoc: any, lookups: any) {
        let result: Array<any> = [];

        if (!sections || !assoc || !lookups) { return result; }

        result.push(SectionField.CreateSingle(
            this.appText('maintenance-buildings'),
            Formatters.getInt(assoc, 'details.maintenanceBuildings'),
            this.isEditing(),
            (props) => (<div />),
        ));

        result.push(SectionField.CreateSingle(
            this.appText('volleyball-courts'),
            Formatters.getInt(assoc, 'details.volleyballCourts'),
            this.isEditing(),
            (props) => (<div />),
        ));

        result.push(SectionField.CreateSingle(
            this.appText('basketball-courts'),
            Formatters.getInt(assoc, 'details.basketballCourts'),
            this.isEditing(),
            (props) => (<div />),
        ));

        result.push(SectionField.CreateSingle(
            this.appText('tennis'),
            Formatters.getInt(assoc, 'details.tennis'),
            this.isEditing(),
            (props) => (<div />),
        ));

        return result;
    }

    // HOA responsible interior checkboxes
    public buildPropertyDetailsAmenitiesResponsibleInteriorsLeftSection(sections: any, assoc: any, lookups: any) {
        let result: Array<any> = [];

        if (!sections || !assoc || !lookups) { return result; }

        result.push(SectionField.CreateSingle(
            this.appText('interior-lighting'),
            Formatters.getBool(assoc, 'details.interiorLighting'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('interior-painting'),
            Formatters.getBool(assoc, 'details.interiorPainting'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('interior-flooring'),
            Formatters.getBool(assoc, 'details.interiorFloors'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        return result;
    }

    public buildPropertyDetailsAmenitiesResponsibleInteriorsMiddleSection(sections: any, assoc: any, lookups: any) {
        let result: Array<any> = [];

        if (!sections || !assoc || !lookups) { return result; }

        result.push(SectionField.CreateSingle(
            this.appText('office'),
            Formatters.getBool(assoc, 'details.office'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('laundry-equipment'),
            Formatters.getBool(assoc, 'details.laundryEquipment'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('elevators'),
            Formatters.getBool(assoc, 'details.interiorElevators'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        return result;
    }

    public buildPropertyDetailsAmenitiesResponsibleInteriorsRightSection(sections: any, assoc: any, lookups: any) {
        let result: Array<any> = [];

        if (!sections || !assoc || !lookups) { return result; }

        result.push(SectionField.CreateSingle(
            this.appText('kitchen'),
            Formatters.getBool(assoc, 'details.kitchen'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('gym'),
            Formatters.getBool(assoc, 'details.gym'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        result.push(SectionField.CreateSingle(
            this.appText('restrooms'),
            Formatters.getBool(assoc, 'details.restrooms'),
            this.isEditing(),
            (props) => (<div />),
            ValColDisplayModes.Boolean
        ));

        return result;
    }

    public buildNotesSection(sections: any, assoc: any) {
        let result: Array<any> = [];

        if (!sections || !assoc) { return result; }

        let notes = _.orderBy(_.get(assoc, 'notes', []), ['created'], ['desc']);
        notes = _.filter(notes, (note) => note.notes !== '' && note.notes !== undefined);
        const assocNotes = (<EntityNotes notes={notes} />);
        if (AppUtilities.isArrayWithLength(notes)) {
            result.push(SectionField.CreateSingle('', assocNotes));
        }

        return result;
    }

    public getSections(assoc: any, lookups: any) {
        let sections = {
            association: new Array(),
            address: new Array(),
            client: new Array(),
            propertyDetailsAmenitiesCoveredLeft: new Array(),
            propertyDetailsAmenitiesCoveredRight: new Array(),
            propertyDetailsAmenitiesResponsibleLeft: new Array(),
            propertyDetailsAmenitiesResponsibleMiddle: new Array(),
            propertyDetailsAmenitiesResponsibleRight: new Array(),
            propertyDetailsAmenitiesResponsibleQtyLeft: new Array(),
            propertyDetailsAmenitiesResponsibleQtyMiddle: new Array(),
            propertyDetailsAmenitiesResponsibleQtyRight: new Array(),
            propertyDetailsAmenitiesResponsibleInteriorsLeft: new Array(),
            propertyDetailsAmenitiesResponsibleInteriorsMiddle: new Array(),
            propertyDetailsAmenitiesResponsibleInteriorsRight: new Array(),
            propertyDetailsConstructionInfo: new Array(),
            propertyDetailsExecutiveSummary: new Array(),
            details: new Array(),
            enabled: false,
            location: null,
            notes: new Array(),
            title: '',
        };

        if (!assoc || !lookups) { return sections; }

        // Sections config
        sections.enabled = true;
        sections.title = this.appTextPageTitleAppendString(`${Formatters.getStr(assoc, 'name')} (${Formatters.getStr(assoc, 'associationId')})`);
        sections.location = _.get(assoc, 'location');

        // Build sections
        sections.association = this.buildAssociationSection(sections, assoc);
        sections.address = this.buildAddressSection(sections, assoc, lookups);
        sections.details = this.buildDetailsSection(sections, assoc, lookups);
        sections.client = this.buildClientContactSection(sections, assoc, lookups);
        sections.propertyDetailsConstructionInfo = this.buildPropertyDetailsConstructionInfoSection(sections, assoc);
        sections.propertyDetailsAmenitiesCoveredLeft = this.buildPropertyDetailsAmenitiesCoveredLeftSection(sections, assoc, lookups);
        sections.propertyDetailsAmenitiesCoveredRight = this.buildPropertyDetailsAmenitiesCoveredRightSection(sections, assoc, lookups);
        sections.propertyDetailsAmenitiesResponsibleLeft = this.buildPropertyDetailsAmenitiesResponsibleLeftSection(sections, assoc, lookups);
        sections.propertyDetailsAmenitiesResponsibleMiddle = this.buildPropertyDetailsAmenitiesResponsibleMiddleSection(sections, assoc, lookups);
        sections.propertyDetailsAmenitiesResponsibleRight = this.buildPropertyDetailsAmenitiesResponsibleRightSection(sections, assoc, lookups);
        sections.propertyDetailsAmenitiesResponsibleQtyLeft = this.buildPropertyDetailsAmenitiesResponsibleQtyLeftSection(sections, assoc, lookups);
        sections.propertyDetailsAmenitiesResponsibleQtyMiddle = this.buildPropertyDetailsAmenitiesResponsibleQtyMiddleSection(sections, assoc, lookups);
        sections.propertyDetailsAmenitiesResponsibleQtyRight = this.buildPropertyDetailsAmenitiesResponsibleQtyRightSection(sections, assoc, lookups);
        sections.propertyDetailsAmenitiesResponsibleInteriorsLeft = this.buildPropertyDetailsAmenitiesResponsibleInteriorsLeftSection(sections, assoc, lookups);
        sections.propertyDetailsAmenitiesResponsibleInteriorsMiddle = this.buildPropertyDetailsAmenitiesResponsibleInteriorsMiddleSection(sections, assoc, lookups);
        sections.propertyDetailsAmenitiesResponsibleInteriorsRight = this.buildPropertyDetailsAmenitiesResponsibleInteriorsRightSection(sections, assoc, lookups);

        sections.notes = this.buildNotesSection(sections, assoc);

        sections.propertyDetailsExecutiveSummary.push(SectionField.CreateSingle(
            '',
            Formatters.getStr(assoc, 'details.executiveSummaryDetails'),
            this.isEditing(),
            (props) => (<div />)
        ));

        return sections;
    }

    // Renderers
    public renderHeaderButtonToolbar = (assoc: any, assocId: any, currentOrderId: any) => {
        let buttonProps = {
            bsSize: 'sm',
            bsStyle: 'info',
        };

        let viewPortalButtonProps = {
            bsStyle: 'info',
            bsSize: 'sm',
            // title: this.appText('view-portal-report'),
        };

        let buttonDangerProps = {
            bsSize: 'sm',
            bsStyle: 'danger',
        };

        const clientId = Formatters.getInt(assoc, 'clientId');

        const viewOrderDisabled = (currentOrderId && Number(currentOrderId) > 0) ? false : true;

        const assocOrder = (viewOrderDisabled) ? (
            <Button {...buttonProps} onClick={e => { this.handleAddOrderButton(e, assocId, clientId); }}>
                <i className="fa fa-shopping-cart" /> {this.appTextCommon('buttons.add-order')}
            </Button>
        ) : (
            <Button {...buttonProps} disabled={viewOrderDisabled} onClick={e => { this.handleViewOrderButton(e, currentOrderId); }}>
                <i className="fa fa-shopping-cart" /> {this.appTextCommon('buttons.view-order')}
            </Button>
        );
        const auth = new Auth();
        const userIsManager = (auth.isAdmin() ? true : false);
        const assocHasNoOrders = (currentOrderId === -1) ? true : false;
        const assocHasNoComponentList = (_.size(assoc.inventorySubgroups) === 1 && _.size(assoc.inventorySubgroups[0]) === 0) ? false : true;
        const ableToShowDeleteAssocButton = (userIsManager); //  && assocHasNoOrders && assocHasNoComponentList);
        const userObjId = auth.getSessionUserObjectId();
        const deleteAssociation = (ableToShowDeleteAssocButton) ? (
            <Button {...buttonDangerProps} onClick={e => { this.handleShowDeleteAssociationModalButton(); }}>
                <i className="fa fa-trash" /> {this.appTextCommon('buttons.delete-association')}
            </Button>
        ) : (
            <span />
        );

        return (
            <div>
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={3}>
                            <ButtonToolbar>
                                <Button {...buttonProps} onClick={e => { this.handleEditButton(e); }}>
                                    <i className="glyphicon glyphicon-edit" /> {this.appText('edit-association')}
                                </Button>
                                {deleteAssociation}
                            </ButtonToolbar>
                        </Col>

                        <Col xs={9}>
                            <ButtonToolbar>

                                {assocOrder}

                                <Button {...buttonProps} onClick={e => { this.handleReserveStudyButton(e); }}>
                                    <i className="fa fa-line-chart" /> {this.appTextCommon('buttons.view-reserve-study')}
                                </Button>
                                <Button {...viewPortalButtonProps} onClick={(e) => { this.handleViewPortalButton(e); }}>
                                    <i className="fa fa-bar-chart" /> {this.appTextCommon('buttons.view-portal')}
                                </Button>
                                <Button {...buttonProps} onClick={e => { this.handleInventoryButton(e); }}>
                                    <i className="glyphicon glyphicon-th-list" /> {this.appTextCommon('buttons.view-inventory')}
                                </Button>
                                <Button {...buttonProps} onClick={e => { this.handlePropertyDescriptionButton(e); }}>
                                    <i className="fa fa-file-pdf-o" /> {this.appTextCommon('buttons.view-property-description')}
                                </Button>
                                <Button {...buttonProps} onClick={e => { this.handleViewDisclosureButton(e); }}>
                                    <i className="fa fa-rocket" /> {this.appTextCommon('buttons.view-disclosure')}
                                </Button>
                            </ButtonToolbar>
                        </Col>
                    </Row>
                </Well>
            </div>
        );
    }

    public renderSelectOptions = (options: Array<any>) => {
        return options.map((c, i) => (<option key={i} value={c.value} disabled={c.disabled}>{c.label}</option>));
    }

    public renderConfirmDeleteAssociationModal = (assoc: any, assocId: any, currentOrderId: any, formData: any) => {
        let modalBody = (<div />);
        const defaultInputProps = { colLabelXs: 5, colInputXs: 3 };
        let inputPropsFullWidthTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            className: 'inventory-detail-input-full-width',
        });

        const auth = new Auth();
        const userObjId = auth.getSessionUserObjectId();

        const userIsManager = (auth.isAdmin() ? true : false);
        const assocHasNoOrders = (currentOrderId === -1) ? true : false;
        const assocHasNoComponentList = ((_.size(assoc.inventorySubgroups) === 1 && _.size(assoc.inventorySubgroups[0]) === 0) || _.size(assoc.inventorySubgroups) === 0) ? false : true;
        const ableToShowDeleteAssocButton = (userIsManager && assocHasNoOrders && assocHasNoComponentList);
        modalBody = (ableToShowDeleteAssocButton) ? (
            <div>
                <div><span><strong>{this.appText('modals.delete-association.are-you-sure')}</strong> </span></div>
                <div><span>{this.appText('modals.delete-association.action-permanent')}</span></div>
                <div><KendoWrappers.KendoTextInputDebounced
                    name="verifyDelete"
                    label={this.appText('modals.delete-association.verify-delete')}
                    type=""
                    required={true}
                    value={formData.verifyDelete}
                    {...inputPropsFullWidthTextDebounced}
                /></div>
            </div>
        ) : (
            <div>
                <span>{this.appText('modals.delete-association.cannot-delete')}</span>
            </div>
        );

        const hideDeleteButton = (ableToShowDeleteAssocButton && (Formatters.getStr(formData, 'verifyDelete') === assocId.toString())) ? (
            <Button bsStyle="danger" onClick={(e) => { this.handleDeleteAssociationButton(e, assocId, userObjId); }}>{this.appText('app.common.buttons.delete', false)}</Button>
        ) : (
            <span />
        );

        return (
            <Modal show={this.state.showModalDeleteAssociation} onHide={() => this.handleCancelAndCloseModalDeleteAssociation()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.delete-association.delete-association')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalBody}
                </Modal.Body>
                <Modal.Footer>
                    {hideDeleteButton}
                    <Button onClick={(e) => { this.handleCancelAndCloseModalDeleteAssociation(); }}>{this.appText('app.common.buttons.cancel', false)}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public getFormData(userIn: any) {
        let result = {
        };

        return _.merge({}, result, this.state.formChanges);
    }

    public getFormChangesCount() {
        return _.size(this.state.formChanges);
    }

    public getFormChangesResetRequired() {
        let resetFormData = false;
        // Props values
        const pa = Formatters.getStr(this.props, 'userObjId');

        // Cached values
        const ca = Formatters.getStr(this.originalFormDataCache, 'userObjId');

        // Determine if formdata state and cache needs reset due to changing associd or invid
        if (pa && ca) {
            resetFormData = (pa !== ca) ? true : false;
        }

        return resetFormData;
    }

    public render() {
        const { lookups, currentOrderId, associationId, portalInvites, ...rest } = this.props;

        const assoc = this.props.association;
        // const currentOrderId = this.props.currentOrderId;
        // const associationId = this.props.match.params.associationId;
        const formData = this.getFormData(assoc);
        if (this.getFormChangesResetRequired() || !_.size(this.originalFormDataCache)) {
            this.originalFormDataCache = _.cloneDeep(formData);
        }

        if (!assoc) { return (<div />); }

        const defaultStr = yup.string().default('');
        const modelSchema = yup.object({
            name: yup.string().required(),
            location: yup.object({
                address1: defaultStr.required('Address 1 is required'),
                address2: defaultStr,
                city: defaultStr.required(),
                stateCode: defaultStr.required(),
                postalCode: defaultStr.required(),
                countryCode: defaultStr,
            }),
            associationType: yup.object({
                associationTypeId: yup.number().required(),
            }),
            company: yup.object({
                companyId: yup.number().required(),
            }),
            client: yup.object({
                clientId: yup.number().required(),
            }),
            contact: yup.object({
                contactId: yup.number().required(),
            }),
            details: yup.object({
                fiscalYearEnd: defaultStr,
                inactive: yup.bool(),
            }),
        });

        const sections = this.getSections(assoc, lookups);

        const topEntityDetailLabelColXs = 4;
        const topEntityDetailValueColXs = 8;

        const midEntityDetailLabelColXs = 6; // 5;
        const midEntityDetailValueColXs = 6; // 7;

        const topSections = (
            <WhiteBoxPageSection pageSectionColXl={10} pageSectionColMd={12} title={sections.title}>
                <Row>
                    {/* Left column */}
                    <Col xs={7} xl={5}>
                        <div>
                            <EntityDetailSection
                                fields={sections.association}
                                title={''}
                                labelColXs={topEntityDetailLabelColXs}
                                valueColXs={topEntityDetailValueColXs}
                                enabled={sections.enabled}
                            />
                        </div>

                        <div>
                            <EntityDetailSection
                                fields={sections.address}
                                title={''}
                                labelColXs={topEntityDetailLabelColXs}
                                valueColXs={topEntityDetailValueColXs}
                                enabled={sections.enabled}
                            />
                        </div>

                        <div>
                            <EntityDetailSection
                                fields={sections.details}
                                title={''}
                                labelColXs={topEntityDetailLabelColXs}
                                valueColXs={topEntityDetailValueColXs}
                                enabled={sections.enabled}
                            />
                        </div>

                        <div>
                            <EntityDetailSection
                                fields={sections.client}
                                title={''}
                                labelColXs={topEntityDetailLabelColXs}
                                valueColXs={topEntityDetailValueColXs}
                                enabled={sections.enabled}
                            />
                        </div>
                    </Col>

                    {/* Right column */}
                    <Col xl={5} xs={5}>
                        <Row>
                            <Col xs={12} className="center-block">
                                <GoogleMapEmbed enabled={sections.enabled} locationDocument={sections.location} />
                            </Col>
                        </Row>

                        { /*
                            <Col xs={12}>
                                <Iframe
                                    url={iframeSrcUrl}
                                    width="400px"
                                    height="320px"
                                    id="iframeId"
                                    className="iframeClassName"
                                    // display="initial"
                                    position="relative"
                                    frameBorder={0}
                                />
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12} className="center-block">
                                <GoogleMapLink enabled={sections.enabled} locationDocument={sections.location} />
                            </Col>
        </Row> */ }

                        <Row>
                            <Col xs={12} className="center-block">
                                <Link to={NavigationLinks.inviteAdd(associationId)}>{this.appText('create-invite-to-portal')}</Link>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );

        const midSections = (
            <Row>
                <Col xs={12} xl={10}>
                    <div className="white-box">
                        <WhiteBoxSection title={this.appText('property-details')}>
                            <Row>
                                <Col xs={4}>
                                    <EntityDetailSection
                                        labelColXs={midEntityDetailLabelColXs}
                                        valueColXs={midEntityDetailValueColXs}
                                        enabled={sections.enabled}
                                        fields={sections.propertyDetailsConstructionInfo}
                                        title={this.appText('construction-info')}
                                    />
                                </Col>

                                <Col xs={4}>
                                    <EntityDetailSection
                                        labelColXs={midEntityDetailLabelColXs}
                                        valueColXs={midEntityDetailValueColXs}
                                        enabled={sections.enabled}
                                        fields={sections.propertyDetailsAmenitiesCoveredLeft}
                                        title={this.appText('common-area-amenities-covered-by-hoa')}
                                    />
                                </Col>

                                <Col xs={4}>
                                    <EntityDetailSection
                                        labelColXs={midEntityDetailLabelColXs}
                                        valueColXs={midEntityDetailValueColXs}
                                        enabled={sections.enabled}
                                        fields={sections.propertyDetailsAmenitiesCoveredRight}
                                        title={this.appText('common-area-amenities-covered-by-hoa')}
                                    />
                                </Col>
                            </Row>
                        </WhiteBoxSection>

                        <WhiteBoxSection title={this.appText('building-exterior-and-grounds')}>
                            <Row>
                                <Col xs={4}>
                                    <EntityDetailSection
                                        labelColXs={midEntityDetailLabelColXs}
                                        valueColXs={midEntityDetailValueColXs}
                                        enabled={sections.enabled}
                                        fields={sections.propertyDetailsAmenitiesResponsibleLeft}
                                        valColDisplay={ValColDisplayModes.Boolean}
                                    />
                                </Col>

                                <Col xs={4}>
                                    <EntityDetailSection
                                        labelColXs={midEntityDetailLabelColXs}
                                        valueColXs={midEntityDetailValueColXs}
                                        enabled={sections.enabled}
                                        fields={sections.propertyDetailsAmenitiesResponsibleMiddle}
                                        valColDisplay={ValColDisplayModes.Boolean}
                                    />
                                </Col>

                                <Col xs={4}>
                                    <EntityDetailSection
                                        labelColXs={midEntityDetailLabelColXs}
                                        valueColXs={midEntityDetailValueColXs}
                                        enabled={sections.enabled}
                                        fields={sections.propertyDetailsAmenitiesResponsibleRight}
                                        valColDisplay={ValColDisplayModes.Boolean}
                                    />
                                </Col>
                            </Row>
                        </WhiteBoxSection>

                        <WhiteBoxSection title="">
                            <Row>
                                <Col xs={4}>
                                    <EntityDetailSection
                                        labelColXs={midEntityDetailLabelColXs}
                                        valueColXs={midEntityDetailValueColXs}
                                        enabled={sections.enabled}
                                        fields={sections.propertyDetailsAmenitiesResponsibleQtyLeft}
                                    />
                                </Col>

                                <Col xs={4}>
                                    <EntityDetailSection
                                        labelColXs={midEntityDetailLabelColXs}
                                        valueColXs={midEntityDetailValueColXs}
                                        enabled={sections.enabled}
                                        fields={sections.propertyDetailsAmenitiesResponsibleQtyMiddle}
                                    />
                                </Col>

                                <Col xs={4}>
                                    <EntityDetailSection
                                        labelColXs={midEntityDetailLabelColXs}
                                        valueColXs={midEntityDetailValueColXs}
                                        enabled={sections.enabled}
                                        fields={sections.propertyDetailsAmenitiesResponsibleQtyRight}
                                    />
                                </Col>
                            </Row>
                        </WhiteBoxSection>

                        <WhiteBoxSection title={this.appText('building-interiors')}>
                            <Row>
                                <Col xs={4}>
                                    <EntityDetailSection
                                        labelColXs={midEntityDetailLabelColXs}
                                        valueColXs={midEntityDetailValueColXs}
                                        enabled={sections.enabled}
                                        fields={sections.propertyDetailsAmenitiesResponsibleInteriorsLeft}
                                        valColDisplay={ValColDisplayModes.Boolean}
                                    />
                                </Col>

                                <Col xs={4}>
                                    <EntityDetailSection
                                        labelColXs={midEntityDetailLabelColXs}
                                        valueColXs={midEntityDetailValueColXs}
                                        enabled={sections.enabled}
                                        fields={sections.propertyDetailsAmenitiesResponsibleInteriorsMiddle}
                                        valColDisplay={ValColDisplayModes.Boolean}
                                    />
                                </Col>

                                <Col xs={4}>
                                    <EntityDetailSection
                                        labelColXs={midEntityDetailLabelColXs}
                                        valueColXs={midEntityDetailValueColXs}
                                        enabled={sections.enabled}
                                        fields={sections.propertyDetailsAmenitiesResponsibleInteriorsRight}
                                        valColDisplay={ValColDisplayModes.Boolean}
                                    />
                                </Col>
                            </Row>
                        </WhiteBoxSection>

                        <WhiteBoxSection title={this.appText('executive-summary-details')}>
                            <Row>
                                <Col xs={12}>
                                    <EntityDetailSection
                                        labelColClassName="left-align"
                                        labelColXs={12}
                                        valueColXs={12}
                                        enabled={sections.enabled}
                                        fields={sections.propertyDetailsExecutiveSummary}
                                    />
                                </Col>
                            </Row>
                        </WhiteBoxSection>
                    </div>
                </Col>
            </Row>
        );

        // auth
        const auth = new Auth();
        const isStaff = (auth.isAdmin() || auth.isEmployee()) ? true : false;
        const userObjId = auth.getSessionUserObjectId();

        const buildPortalManagers = (pi: any) => {
            const validPortalInvites: any = UserUtilities.GetValidPortalInvites(pi);

            const filterAdminUser = UserUtilities.FilterPortalInvites(validPortalInvites, PortalRoles.PortalAdmin.RoleName);
            let usersAdmin = _.map(filterAdminUser, (o) => {
                const portalUserObjId = _.get(o, 'user.userObjectId', '');
                const userLink = NavigationLinks.userDetailObjId(portalUserObjId);
                return UserUtilities.BuildInviteLinkToUserProfile(o, userLink, false, false);
            });

            if (!_.size(usersAdmin)) {
                usersAdmin = [UserUtilities.BuildEmptyPortalInvites(associationId, PortalRoles.PortalAdmin.RoleName, this.appText('app.portal-invites.user-groups.none-assigned', false))];
            }

            return usersAdmin;
        };

        const buildPortalAllowedUsers = (pi: any) => {
            const validPortalInvites: any = UserUtilities.GetValidPortalInvites(pi);

            const filterAllowedUser = UserUtilities.FilterPortalInvites(validPortalInvites, PortalRoles.PortalUser.RoleName);
            let usersAllowed = _.map(filterAllowedUser, (o) => {
                const portalUserObjId = _.get(o, 'user.userObjectId', '');
                const userLink = NavigationLinks.userDetailObjId(portalUserObjId);
                return UserUtilities.BuildInviteLinkToUserProfile(o, userLink, false, false);
            });

            if (!_.size(usersAllowed)) {
                usersAllowed = [UserUtilities.BuildEmptyPortalInvites(associationId, PortalRoles.PortalUser.RoleName, this.appText('app.portal-invites.user-groups.none-assigned', false))];
            }

            return usersAllowed;
        };

        const buildPortalDeactivatedUsers = (pi: any) => {
            const invalidPortalInvites: any = UserUtilities.GetInvalidPortalInvites(pi);

            let usersDeactive = _.map(invalidPortalInvites, (o) => {
                const portalUserObjId = _.get(o, 'user.userObjectId', '');
                const userLink = NavigationLinks.userDetailObjId(portalUserObjId);

                let res = (
                    <div key={`div-portalinvite-${_.get(o, 'association.associationObjectId', '')}-${_.get(o, 'user.userObjectId', '')}`}>
                        {UserUtilities.BuildInviteLinkToUserProfile(o, userLink, true, false)}
                    </div>
                );

                return res;
            });

            if (!_.size(usersDeactive)) {
                usersDeactive = [UserUtilities.BuildEmptyPortalInvites(associationId, 'deactivated', this.appText('app.portal-invites.user-groups.none-deactivated', false))];
            }

            return usersDeactive;
        };

        const portalAccessSection = (
            <WhiteBoxPageSection pageSectionColXl={10} pageSectionColMd={12} title={this.appText('app.portal-invites.manage-user-access', false)}>
                <Row>
                    <Col xs={12} md={4}>
                        <Row className="entity-detail-section">
                            <Col xs={12}>
                                <h5><b>{this.appText('app.portal-invites.user-groups.user-managers', false)}&nbsp;</b></h5>
                                {buildPortalManagers(portalInvites)}
                            </Col>
                        </Row>
                    </Col>

                    <Col xs={12} md={4}>
                        <Row className="entity-detail-section">
                            <Col xs={12}>
                                <h5><b>{this.appText('app.portal-invites.user-groups.allowed-users', false)}&nbsp;</b></h5>
                                {buildPortalAllowedUsers(portalInvites)}
                            </Col>
                        </Row>
                    </Col>

                    <Col xs={12} md={4}>
                        <Row className="entity-detail-section">
                            <Col xs={12}>
                                <h5><b>{this.appText('app.portal-invites.user-groups.deactivated-users', false)}&nbsp;</b></h5>
                                {buildPortalDeactivatedUsers(portalInvites)}
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );

        const lowerSections = (
            <div>
                <WhiteBoxPageSection pageSectionColXl={10} pageSectionColMd={12} title={this.appText('association-notes')}>
                    <Row>
                        <Col xs={12}>
                            <EntityDetailSection
                                fields={sections.notes}
                                labelColXs={0}
                                valueColXs={12}
                                enabled={sections.enabled}
                            />
                        </Col>
                    </Row>
                </WhiteBoxPageSection>
            </div>
        );

        const HorizontalForm = ({ ...a }) => <RBForm horizontal={true} {...a} />;

        const def = _.cloneDeep(assoc);

        const form = (
            <Form schema={modelSchema} component={HorizontalForm} defaultValue={def}>
                <Row>
                    <Col xs={12} xl={10}>
                        <div>
                            {this.renderHeaderButtonToolbar(assoc, associationId, currentOrderId)}
                        </div>
                    </Col>
                </Row>
                {this.renderConfirmDeleteAssociationModal(assoc, associationId, currentOrderId, formData)}
                {topSections}
                {midSections}
                {lowerSections}
                {portalAccessSection}
            </Form>
        );

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={sections.title} {...rest}>
                {form}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrlLookups = () => ApiEndpoints.getLookupsByKeyUrl([
    LK.LookupKeys.Companies,
    LK.LookupKeys.Regions,
    LK.LookupKeys.StreetTypes,
    LK.LookupKeys.AssociationTypes,
    LK.LookupKeys.Clients,
    LK.LookupKeys.RoofTypes,
    LK.LookupKeys.SidingTypes,
    LK.LookupKeys.FencingTypes,
    LK.LookupKeys.InteriorHallTypes,
    LK.LookupKeys.IrrigationTypes,
    LK.LookupKeys.SecuredTypes,
    LK.LookupKeys.MechanicalTypes,
    LK.LookupKeys.ElevatorTypes,
    LK.LookupKeys.RecRoomTypes,
]);

const getApiUrl = props => ApiEndpoints.getAssociationDetailUrl(props.match.params.associationId);

const mapPropsToConfigs = (props) => {
    return [
        ReduxQueries.Queries.getAssociationDetailRequest(
            getApiUrl(props),
            ReduxQueries.PropsAccessToken(props),
        ),

        ReduxQueries.Queries.getLookupsByKeyRequest(
            getApiUrlLookups(),
            ReduxQueries.PropsAccessToken(props)
        ),
    ];
};

const mapStateToProps = (state, props) => {
    const associationId = _.get(props, 'match.params.associationId', 0);

    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(props), state, props, {
        associationId: associationId,
        association: state.entities.associationDetailAssociation,
        currentOrderId: state.entities.associationDetailAssociationCurrentOrderId,
        portalInvites: state.entities.associationDetailAssociationPortalInvites,
    });
};

const mapDispatchToProps = {
    newOrder: MutateActions.newOrderMutateAsync,
    deleteAssociation: MutateActions.deleteAssociationMutateAsync,
    navigateTo: (path) => push(path)
};

const AssociationContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(Association);

export default withRouter(AssociationContainer);



// WEBPACK FOOTER //
// ./src/association/index.tsx